import {Component, Inject, inject, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {FormHelper, KeyValue} from "../../../../../../projects/vendors/src/lib/services/form-helper";
import {ApiBaseService} from "../../../../../../projects/vendors/src/lib/services/api-base.service";
import {StorageService} from "../../../../../../projects/vendors/src/lib/services/storage.service";
import {NgxPrintService, PrintOptions} from "ngx-print";
import {RestaurantDetailResponse} from "../../../../@domain/resturant-detail";
import {PaymentMethods} from "../../../../@application/enum/payment-methods";
import {OrderType} from "../../../../@application/enum/order-type.enums";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {FoodItemQuantity} from "../../../../@domain/food-item-quantity";
import {OnGoingOrder} from "../../../../@domain/on-going-order";
import {firstValueFrom, Observable} from "rxjs";
import JsBarcode from "jsbarcode";
import {TaxValueResponse} from "../../../../@domain/tax-value";
import {ApiEnum} from "../../../../@application/enum/api.enum";
import {ServiceChargeResponse} from "../../../../@domain/service-charge";

@Component({
    selector: 'app-order-bill-dialog',
    templateUrl: './order-bill-dialog.component.html',
    styleUrl: './order-bill-dialog.component.scss',
    standalone: false
})
export class OrderBillDialogComponent implements OnInit{
  discountAdded: boolean = false;
  paidAdded: boolean = false;
  billForm!: FormGroup;
  serviceChargeChecked: boolean = true;
  totalAmount: number = 0;
  discount: number = 0;
  paidAmount: number = 0;
  grandTotal: number = 0;
  totalPrice: number = 0;
  balance: number = 0;
  serviceCharge: number = 0;
  tax: number = 0;
  paymentMethodKeyValue = [] as KeyValue[]
  taxChecked = true;
  placeholder = 'Enter remark';
  date = new Date();
  private apiBaseService = inject(ApiBaseService)
  private storageService = inject(StorageService)
  private printService = inject(NgxPrintService)
  restaurantDetail = {} as RestaurantDetailResponse
  paymentMethod = PaymentMethods
  orderType = OrderType
  foodItemQuantity: FoodItemQuantity[] = []
  userName!: string
  px2mmFactor!: number;
  // 2.5in receipt printer instead of the 3in default - see docs/RECEIPT_WIDTH.md
  smallTenant = 'b6778fd2-1972-4683-9998-3a9f3fb65fb0'
  tenantId!: string
  shouldApplyWidth = false;
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      onGoingOrder: OnGoingOrder,
      foods: any[]
    },
    private dialogRef: MatDialogRef<OrderBillDialogComponent>) {
    this.paymentMethodKeyValue = FormHelper.PaymentMethodsList()
    this.totalPrice = this.data.onGoingOrder.cartResponseDetails.reduce((acc, curr) => acc + curr.totalPrice, 0)
  }

  formInitializer() {
    this.userName = this.storageService.getFromSessionStorage('userDetails')?.givenName
    this.billForm = new FormGroup({
      orderId: new FormControl(this.data.onGoingOrder.orderId),
      paymentMethod: new FormControl(null, [Validators.required]),
      totalOrderAmount: new FormControl(this.totalPrice, [Validators.required]),
      paidAmount: new FormControl(null, [Validators.required, Validators.min(0)]),
      balance: new FormControl(null),
      netAmount: new FormControl(null, [Validators.min(0)]),
      remarks: new FormControl(null),
      marginPercentage: new FormControl(this.data.onGoingOrder.marginPercentage),
      discountDetail: new FormGroup({
        discountPercentage: new FormControl(null, [Validators.min(0),
          Validators.max(this.data.onGoingOrder.marginPercentage)]),
        discountAmount: new FormControl(null, [Validators.min(0)]),
        amountBeforeDiscount: new FormControl(this.totalPrice),
        amountAfterDiscount: new FormControl(null),
      }),
      taxDetail: new FormGroup({
        taxPercentage: new FormControl(null),
        taxAmount: new FormControl(null),
        amountBeforeTax: new FormControl(this.totalPrice),
        amountAfterTax: new FormControl(null),
      }),
      serviceChargeDetail: new FormGroup({
        serviceChargePercentage: new FormControl(null),
        serviceChargeAmount: new FormControl(null),
        amountBeforeServiceCharge: new FormControl(this.totalPrice),
        amountAfterServiceCharge: new FormControl(null),
      }),
    });
  }

  async ngOnInit() {
    this.tenantId = this.storageService.getFromSessionStorage('tenantId');
    this.shouldApplyWidth = this.tenantId === this.smallTenant;
    this.px2mmFactor = this.calcPx2MmFactor();
    this.generateBarcode();
    this.getFoodItemQuantity()
    this.formInitializer()
    this.restaurantDetail = await firstValueFrom(this.getRestaurantData())
    const taxResponse = await firstValueFrom(this.getTaxAmount())
    if (taxResponse.taxValue !== null) {
      this.billForm.get('taxDetail.taxPercentage')?.setValue(taxResponse.taxValue.tax)
    }
    const scResponse = await firstValueFrom(this.getServiceChargeAmount())
    if (scResponse.serviceCharge !== null) {
      this.billForm.get('serviceChargeDetail.serviceChargePercentage')?.setValue(scResponse.serviceCharge.serviceCharge)
    }
    this.calculateTotalAmount();
    this.updateCalculations();
    this.billForm.get('discountDetail.discountPercentage')?.valueChanges.subscribe(() => this.updateCalculations());
    this.billForm.get('paidAmount')?.valueChanges.subscribe(() => this.updateCalculations());
    this.billForm.get('paymentMethod')?.valueChanges
      .subscribe({
        next: (value) => {
          if (value === 1 || value === 3) {
            this.placeholder = 'Enter remark';
          } else if (value === 2) {
            this.placeholder = 'Enter the last 4 digits of the card';
          } else if (value === 4) {
            this.placeholder = 'Enter payment method';
          }
        }
      })
  }

  private generateBarcode() {
    JsBarcode('#barcode', this.data.onGoingOrder.orderNumber, {
      format: 'code128', // default
      height: 10 * this.px2mmFactor, // 10mm
      width: 2.3,
      // displayValue: false,
      text: this.data.onGoingOrder.orderNumber,
      background: 'transparent',
      font: 'monospace',
      fontOptions: 'normal',
      fontSize: 16,
      lineColor: 'black',
      margin: 5 * this.px2mmFactor, // 5mm
      textMargin: 2 * this.px2mmFactor, // 2mm
      // textAlign: 'right',
      // textPosition: 'top',
    });
  }


  private calcPx2MmFactor() {
    let e = document.createElement('div');
    e.style.position = 'absolute';
    e.style.width = '100mm';
    document.body.appendChild(e);
    let rect = e.getBoundingClientRect();
    document.body.removeChild(e);
    return rect.width / 100;
  }

  getTaxAmount(): Observable<TaxValueResponse> {
    return this.apiBaseService.GET<TaxValueResponse>([ApiEnum.RestaurantModule, ApiEnum.SystemSetting, ApiEnum.TaxValue])
  }

  getServiceChargeAmount(): Observable<ServiceChargeResponse> {
    return this.apiBaseService.GET<ServiceChargeResponse>([ApiEnum.RestaurantModule, ApiEnum.SystemSetting, ApiEnum.ServiceCharge])
  }

  getRestaurantData(): Observable<RestaurantDetailResponse> {
    return this.apiBaseService.GET<RestaurantDetailResponse>([ApiEnum.RestaurantModule, ApiEnum.SystemSetting, ApiEnum.RestaurantDetail])
  }


  calculateTotalAmount() {
    // this.totalAmount = this.data.onGoingOrder.totalPrice
    this.totalAmount = this.data.onGoingOrder.cartResponseDetails.reduce((acc, curr) => acc + curr.totalPrice, 0)
  }

  calculateGrandTotal() {
    const discount = this.billForm.get('discountDetail.discountPercentage')?.value || 0;
    this.discount = (this.totalAmount * discount) / 100;
    let serviceChargeAmount = 0;
    let taxAmount = 0;
    if (this.serviceChargeChecked) {
      serviceChargeAmount = this.serviceChargeChecked ? (this.totalAmount * this.billForm.get('serviceChargeDetail.serviceChargePercentage')?.value) / 100 : 0;
      this.billForm.get('serviceChargeDetail.serviceChargeAmount')?.setValue(serviceChargeAmount);
      this.billForm.get('serviceChargeDetail.amountAfterServiceCharge')?.setValue(this.totalAmount + serviceChargeAmount);
    }
    if (this.taxChecked) {
      taxAmount = (this.totalAmount * this.billForm.get('taxDetail.taxPercentage')?.value) / 100;
      this.billForm.get('taxDetail.taxAmount')?.setValue(taxAmount);
      this.billForm.get('taxDetail.amountAfterTax')?.setValue(this.totalAmount + taxAmount);
    }
    if (this.discountAdded) {
      this.billForm.get('discountDetail.discountAmount')?.setValue(this.discount);
      this.billForm.get('discountDetail.amountAfterDiscount')?.setValue(this.totalAmount - this.discount);
    }
    this.grandTotal = this.totalAmount - this.discount + serviceChargeAmount + taxAmount;
    this.billForm.get('netAmount')?.setValue(this.grandTotal);
    this.serviceCharge = serviceChargeAmount;
    this.tax = taxAmount;
  }

  updateCalculations() {
    this.calculateGrandTotal();
    const paidAmount = this.billForm.get('paidAmount')?.value || 0;
    this.balance = paidAmount - this.grandTotal;
    if (paidAmount > 0) {
      this.billForm.get('balance')?.setValue(+parseFloat(String(this.balance)).toFixed(2));
    }
  }

  onDiscountAdded() {
    this.discountAdded = this.billForm.get('discountDetail.discountPercentage')?.value > 0;
    if (this.billForm.get('discountDetail.discountPercentage')?.value <= this.data.onGoingOrder.marginPercentage) {
      this.updateCalculations();
    }
  }

  onPaidAmount() {
    this.paidAdded = true;
    this.updateCalculations();
  }

  onCloseOrder() {
    if (this.billForm.valid) {
      this.submitForm()
    } else {
      Object.values(this.billForm.controls).forEach(control => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({onlySelf: true})
        }
      })
    }
  }

  private submitForm() {
    // Read the value being sent, not the discountAdded flag - the flag goes stale when the
    // field is cleared and a null percentage fails to deserialise as a decimal server-side.
    if (!this.billForm.get('discountDetail.discountPercentage')?.value) {
      this.billForm.get('discountDetail.discountPercentage')?.setValue(0)
      this.billForm.get('discountDetail.discountAmount')?.setValue(0)
      this.billForm.get('discountDetail.amountAfterDiscount')?.setValue(0)
    }
    if (!this.serviceChargeChecked) {
      this.billForm.get('serviceChargeDetail.serviceChargePercentage')?.setValue(0)
      this.billForm.get('serviceChargeDetail.amountAfterServiceCharge')?.setValue(0)
      this.billForm.get('serviceChargeDetail.serviceChargeAmount')?.setValue(0)
    }
    if (!this.taxChecked) {
      this.billForm.get('taxDetail.taxPercentage')?.setValue(0)
      this.billForm.get('taxDetail.amountAfterTax')?.setValue(0)
      this.billForm.get('taxDetail.taxAmount')?.setValue(0)
    }
    // this.printBill()
    this.apiBaseService.POST_API([ApiEnum.RestaurantModule, ApiEnum.OrderBill], this.billForm.value,
      true, true, 'Order has been closed successfully')
      .subscribe({
        next: (res) => {
          this.dialogRef.close(res)
        }
      })
  }

  private printBill() {
    const customPrintOptions: PrintOptions = new PrintOptions({
      printSectionId: 'print-section',
      useExistingCss: true,
      // Add any other print options as needed
    });
    this.printService.print(customPrintOptions)
  }

  private getFoodItemQuantity(): void {
    this.apiBaseService.GET<FoodItemQuantity[]>([ApiEnum.FoodModule, ApiEnum.FoodItemQuantityTag])
      .subscribe(res => {
        this.foodItemQuantity = res
      });
  }

  foodItemCode(foodId: string): string {
    return this.data.foods.find(food => food.id === foodId)?.foodItemCode;
  }
}
